import React from 'react';
import axios from 'axios';
import { decode } from 'html-entities';
import { Thumbnail } from 'components/Thumbnail';
import Tooltip from 'components/Tooltip';
import Tag from 'components/Tag';
import { OrgFeatures } from '@bb/domain/dist/types';

import { AppExternalController } from 'api';
import {
  SearchType,
  ColumnOptions,
  FormattedListingType,
  ListingResponseType,
} from './types';
import ListingRowActions from './ListingRowActions';
import { BrokerageInfo, ListingInfo } from './components';
import { availabilityOrder } from './constants';

export const mapExternalListingResultsToTable = (
  listing: any,
  forceBlur: () => void,
  length: number,
) => {
  const {
    id,
    thumbnail,
    fullAddress,
    line1,
    line2,
    city,
    province,
    list_price: price,
    status,
    mls_numbers,
    availability,
    location,
    org,
    dom,
    acceptsRegisteredOffers,
    noMoreShowings,
    team,
    preBookingInformation,
  } = listing;
  const availabilityLabel = availability.replace(/_/g, ' ');
  return {
    id,
    thumbnail,
    key: id,
    availability: availabilityLabel as any,
    location,
    fullAddress: decode(fullAddress),
    line1,
    line2,
    city,
    province,
    org,
    dom,
    noMoreShowings,
    team,
    price,
    preBookingInformation,
    status: {
      type: status,
      mlsNumbers: mls_numbers,
    },
    orgId: org.id,
    acceptsRegisteredOffers,
    forceBlur,
    listing: { ...listing, org: org.id },
    value:
      (availabilityOrder.indexOf(availabilityLabel) + length + 1) %
      (length + 1),
    showScheduleBtn: org.features.includes('APPOINTMENT'),
    listingOrgHasFeature: (feature: OrgFeatures) =>
      listing.org.features.includes(feature),
  };
};

const externalListingSearch: (
  options: ColumnOptions,
  setActiveRow: React.Dispatch<React.SetStateAction<null | number>>,
) => SearchType<FormattedListingType> = (options, setActiveRow) => ({
  columns: [
    {
      title: '',
      dataIndex: 'listing',
      key: 'preview',
      className: 'eye-column cross-org',
      width: 85,
      render: (listing: FormattedListingType['listing']) => (
        <Thumbnail listing={listing} src={listing.thumbnail} />
      ),
    },
    {
      title: '',
      key: 'address',
      className: 'address-column cross-org',
      width: '50%',
      render: (
        _,
        { line1, line2, province, city, availability, price, status, dom },
      ) => (
        <>
          <ListingInfo
            line1={line1}
            line2={line2}
            province={province}
            city={city}
            availability={availability}
            price={price}
            status={status}
            dom={dom}
          />
        </>
      ),
    },
    {
      title: '',
      key: 'tag',
      className: 'tag-column cross-org',
      render: (listing, { auth }) =>
        listing.org.isUnclaimed ? (
          <Tooltip title="This listing is not represented by BrokerBay. Please ensure to book via the correct showing service.">
            <Tag color="red" data-cy="tag-unclaimed-listing">
              Not BrokerBay
            </Tag>
          </Tooltip>
        ) : null,
    },
    {
      title: '',
      key: 'org',
      className: 'org-column',
      render: (_, { org }) =>
        options.lg && (
          <BrokerageInfo
            orgName={org.name}
            orgLogoUrl={org.logoUrl}
            dataCy="listing-org"
          />
        ),
    },
    {
      title: '',
      dataIndex: 'id',
      key: 'button',
      className: 'action-buttons-column',
      render: (
        id: string,
        {
          forceBlur,
          acceptsRegisteredOffers,
          showScheduleBtn,
          thumbnail,
          line1,
          line2,
          province,
          city,
          availability,
          price,
          status,
          dom,
          noMoreShowings,
          org,
          listingOrgHasFeature,
          preBookingInformation,
        },
        index,
      ) => (
        <ListingRowActions
          external
          id={id}
          forceBlur={forceBlur}
          acceptsRegisteredOffers={acceptsRegisteredOffers}
          showScheduleBtn={showScheduleBtn}
          index={index}
          options={options}
          noMoreShowings={noMoreShowings}
          hasFeature={listingOrgHasFeature}
          listing={{
            id,
            thumbnail,
            line1,
            line2,
            province,
            city,
            availability,
            list_price: price,
            status: status?.type,
            mls_numbers: status?.mlsNumbers,
            dom,
            org: {
              name: org.name,
              logoUrl: org.logoUrl,
              isUnclaimed: org.isUnclaimed,
            },
            preBookingInformation,
          }}
        />
      ),
    },
  ],
  search: async (debouncedQuery, forceBlur, { dashboardId, retsId }) => {
    const url = `/external/${dashboardId ? 'dash' : 'rets'}/${
      dashboardId || retsId
    }/search/listings`;

    const {
      data: { hits },
    } = await axios.get<ListingResponseType>(url, {
      params: {
        input: debouncedQuery,
      },
    });
    const data = hits.map(
      ({
        id,
        line1,
        line2,
        thumbnail,
        fullAddress,
        province,
        city,
        reps,
        list_price,
        type,
        status,
        mls_numbers,
        availability,
        location,
        org,
        dom,
        acceptsRegisteredOffers,
        noMoreShowings,
        team,
        preBookingInformation,
      }) => ({
        id,
        fullAddress,
        thumbnail,
        line1,
        line2,
        province,
        city,
        reps,
        list_price,
        type,
        status,
        mls_numbers,
        availability,
        location,
        org,
        dom,
        acceptsRegisteredOffers,
        noMoreShowings,
        team,
        preBookingInformation,
      }),
    );

    const { length } = data;
    return data
      .map(listing =>
        mapExternalListingResultsToTable(listing, forceBlur, length),
      )
      .sort((a, b) => a.value - b.value);
  },
  onConfirm: async (itemSelected, { forceBlur, history, location }) => {
    const { id } = itemSelected;
    const { pathname } = location;
    const [, identifier, externalId] = pathname.split('/');
    const newExternalId =
      await AppExternalController.createNewDashboardForListing(
        identifier,
        externalId,
        id,
      );
    history.push(`/${identifier}/${newExternalId}/listing/`);
    forceBlur();
  },
  createOnRow:
    ({ forceBlur, history, location }) =>
    (item, index) => ({
      onClick: async () => {
        const { id } = item;
        const { pathname } = location;
        const [, identifier, externalId] = pathname.split('/');
        const newExternalId =
          await AppExternalController.createNewDashboardForListing(
            identifier,
            externalId,
            id,
          );
        history.push(`/${identifier}/${newExternalId}/listing/`);
        forceBlur();
      },
      onMouseMove: () => setActiveRow(index),
      onMouseLeave: () => setActiveRow(null),
    }),
});

export default externalListingSearch;
